/**
 * Me resource: the signed-in user's own profile and membership in a silo.
 *
 * The user's own profile is the `profiles.get` read for their profile key, so
 * it shares its cache with every other read of that profile.
 */
import type { IsoLocale } from '@repo/shared/locale';
import type { DetailedUserProfile } from '@repo/shared/orpc/procedures/profiles';
import type { UserProfile } from '@repo/shared/orpc/types';
import { useSuspenseQueries } from '@tanstack/react-query';
import type { SiloMember } from 'component/silo-member-context';
import { useApiQueryUtils } from 'system/api-client-context';
import { useSuspenseSession } from 'util/auth-client';
import { usePersonaKey } from 'util/datastore';

import { useProfile } from './profile';

/**
 * The signed-in user's membership in a silo, or `null` when signed out,
 * suspending until it loads. Before setup, its profile is the one their account
 * would get, with `hasProfile: false`.
 *
 * @privateRemarks
 * Reads the membership from the user's own profile, whose key is their user
 * ID, which holds for every profile the server creates. Consider a read that
 * finds the caller's membership from the access token that authorizes it.
 */
export function useSuspenseSiloMember({ siloKey, locale }: { siloKey: string; locale: IsoLocale }): SiloMember | null {
    const utils = useApiQueryUtils();
    const session = useSuspenseSession();
    const userId = session.data?.user.id;
    const profileKeys = userId ? [userId] : [];
    return useSuspenseQueries({
        queries: profileKeys.map((profileKey) =>
            utils.profiles.get.queryOptions({ input: { siloKey, profileKey, locale }, select: toSiloMember }),
        ),
        combine: (results) => results[0]?.data ?? null,
    });
}

function toSiloMember({ roles = [], ...profile }: DetailedUserProfile): SiloMember {
    return { profile, roles };
}

/**
 * The current caller's own display profile — `null` if not signed in. Reads
 * `useProfile` against the caller's own key, the same as any other profile
 * read.
 */
export function useMyProfile(): UserProfile | null {
    const personaKey = usePersonaKey();
    return useProfile(personaKey).profile;
}
